import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  ITaskDenormalized,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { Typography } from '@material-ui/core';
import { useMemo } from 'react';
import { TransparentText } from '../../../../../../components/DebugText';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import {
  filterTasksByStatus,
  getMessageStatuses,
  useGroupedTaskAndMessages
} from '../../services/items';
import { getIndividualInstanceUrl } from '../../services/routes';
import {
  AllCurrentTasksDone,
  DynamicallyTailoredListHint,
  NoArchivedMessages,
  NoCompletedTasks,
  NoCurrentTasksButOverdue,
  NoOptionalTasks,
  NoOverdueTasks,
  NoUnarchivedMessages,
  NoUpcomingMessages,
  NoUpcomingTasks,
  OptionalTasksHint
} from '../EmptyStatesAndHints';
import { InboxNavItemKey } from '../Nav/InboxNav';
import { TasksAndMessages } from '../TasksAndMessages';

const Title: React.FC = ({ children }) => (
  <FlexContainerVertical fullWidth spacing={2}>
    <Typography variant="h5">{children}</Typography>
  </FlexContainerVertical>
);

const Body = ({
  header,
  body,
  emptyState,
  isEmpty
}: {
  header: React.ReactNode;
  body: React.ReactNode;
  emptyState: React.ReactNode;
  isEmpty: boolean;
}) => {
  return (
    <FlexContainerVertical fullWidth spacing={2}>
      {header}
      {isEmpty ? emptyState : body}
    </FlexContainerVertical>
  );
};

export const InstanceMainInboxNav = ({
  instance,
  actorAccessToken,
  instanceAccessToken,
  g,
  returnTo
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  actorAccessToken: string;
  instanceAccessToken: string;
  g: InboxNavItemKey;
  returnTo?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { anyOverdueTasks, anyTasksAtAll } = useMemo(() => {
    return {
      anyOverdueTasks: !!filterTasksByStatus(instance, [TaskStatus.LATE])
        .length,
      anyTasksAtAll: !!instance.data.tasks.length
    };
  }, [instance]);
  const items = useMemo<{
    tasks: ITaskDenormalized[];
    msgs: IExperienceMessageDenormalized[];
  }>(() => {
    const msgStatuses = getMessageStatuses(instance);
    const allMsgs = instance.data.messages;
    if (g === 'everything') {
      return {
        tasks: filterTasksByStatus(instance, [
          TaskStatus.RUNNING,
          TaskStatus.LATE
        ]),
        msgs: allMsgs.filter((msg) => msgStatuses[msg.templateId] === 'CURRENT')
      };
    }
    if (g === 'complete') {
      return {
        tasks: filterTasksByStatus(instance, [
          TaskStatus.COMPLETE,
          TaskStatus.SKIPPED
        ]),
        msgs: []
      };
    }
    if (g === 'todo') {
      return {
        tasks: filterTasksByStatus(instance, [TaskStatus.RUNNING]),
        msgs: []
      };
    }
    if (g === 'upcoming') {
      return {
        tasks: filterTasksByStatus(instance, [TaskStatus.NOT_STARTED]),
        msgs: []
      };
    }
    if (g === 'optional') {
      return {
        tasks: filterTasksByStatus(instance, [TaskStatus.PAST_DEADLINE]),
        msgs: []
      };
    }
    if (g === 'overdue') {
      return {
        tasks: filterTasksByStatus(instance, [TaskStatus.LATE]),
        msgs: []
      };
    }
    if (g === 'msgs') {
      return {
        tasks: [],
        msgs: allMsgs.filter((msg) => msgStatuses[msg.templateId] === 'CURRENT')
      };
    }
    if (g === 'archived-msgs') {
      return {
        tasks: [],
        msgs: allMsgs.filter(
          (msg) => msgStatuses[msg.templateId] === 'ARCHIVED'
        )
      };
    }
    if (g === 'upcoming-msgs') {
      return {
        tasks: [],
        msgs: allMsgs.filter((msg) => msgStatuses[msg.templateId] === 'LATER')
      };
    }
    return { tasks: [], msgs: [] };
  }, [instance, g]);
  const tasksAndMessages = useGroupedTaskAndMessages(items.tasks, items.msgs);
  const isEmpty = !tasksAndMessages.length;
  if (g === 'everything') {
    return (
      <Body
        header={
          <Title>{<TransparentText>{t('In progress')}</TransparentText>}</Title>
        }
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={
          <AllCurrentTasksDone
            anyTasksAtAll={anyTasksAtAll}
            singleInstanceMode
          />
        }
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'complete') {
    return (
      <Body
        header={<Title>{t('Completed tasks')}</Title>}
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={<NoCompletedTasks />}
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'todo') {
    return (
      <Body
        header={<Title>{t('To do tasks')}</Title>}
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={
          anyOverdueTasks ? (
            <NoCurrentTasksButOverdue
              to={getIndividualInstanceUrl(
                actorAccessToken,
                instanceAccessToken,
                {
                  g: 'overdue'
                }
              )}
            />
          ) : (
            <AllCurrentTasksDone
              anyTasksAtAll={anyTasksAtAll}
              singleInstanceMode
            />
          )
        }
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'upcoming') {
    return (
      <Body
        header={<Title>{t('For later')}</Title>}
        body={
          <>
            <DynamicallyTailoredListHint />
            <TasksAndMessages
              ds={tasksAndMessages}
              actorAccessToken={actorAccessToken}
              instanceAccessToken={instanceAccessToken}
              returnTo={returnTo}
              archiveControls
            />
          </>
        }
        emptyState={<NoUpcomingTasks />}
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'optional') {
    return (
      <Body
        header={<Title>{t('Optional tasks')}</Title>}
        body={
          <>
            <OptionalTasksHint />
            <TasksAndMessages
              ds={tasksAndMessages}
              actorAccessToken={actorAccessToken}
              instanceAccessToken={instanceAccessToken}
              returnTo={returnTo}
              archiveControls
            />
          </>
        }
        emptyState={<NoOptionalTasks />}
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'overdue') {
    return (
      <Body
        header={<Title>{t('Overdue tasks')}</Title>}
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={<NoOverdueTasks />}
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'msgs') {
    return (
      <Body
        header={<Title>{t('Messages')}</Title>}
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={<NoUnarchivedMessages />}
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'archived-msgs') {
    return (
      <Body
        header={<Title>{t('Archived messages')}</Title>}
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={<NoArchivedMessages singleInstanceMode />}
        isEmpty={isEmpty}
      />
    );
  }
  if (g === 'upcoming-msgs') {
    return (
      <Body
        header={<Title>{t('Upcoming messages')}</Title>}
        body={
          <TasksAndMessages
            ds={tasksAndMessages}
            actorAccessToken={actorAccessToken}
            instanceAccessToken={instanceAccessToken}
            returnTo={returnTo}
            archiveControls
          />
        }
        emptyState={<NoUpcomingMessages />}
        isEmpty={isEmpty}
      />
    );
  }
  console.log('Group not recognized', g);
  return null;
};
